/**
 * EnemySpriteRegistry - Loads PixelLab exports for enemy sprites.
 *
 * Expected folder structure per enemy:
 *   public/sprites/enemies/{typeId}/
 *     metadata.json                         — PixelLab export metadata
 *     rotations/{direction}.png             — 8 static directional poses
 *     animations/{animName}/{dir}/frame_NNN.png — animation frames
 *
 * Direction mapping (PixelLab → game):
 *   south→down, south-east→down-right, east→right, north-east→up-right,
 *   north→up, north-west→up-left, west→left, south-west→down-left
 *
 * Falls back to procedural rendering if no export folder exists.
 */

import { ZONES, ZoneType } from '@shared/world/Zone';
import type { Direction8 } from './SpriteSheet';
import { debugLog } from '@shared/utils/Logger';

/** PixelLab direction names → our Direction8 (load all 8 from PixelLab) */
const PL_TO_DIR: Record<string, Direction8> = {
  'south': 'down',
  'south-east': 'down-right',
  'east': 'right',
  'north-east': 'up-right',
  'north': 'up',
  'north-west': 'up-left',
  'west': 'left',
  'south-west': 'down-left',
};

/** Snap 8 game directions to 4 cardinal directions for sprite display */
const DIR8_TO_DIR4: Record<Direction8, Direction8> = {
  'down':       'down',
  'down-left':  'left',
  'down-right': 'right',
  'left':       'left',
  'right':      'right',
  'up-left':    'up',
  'up-right':   'up',
  'up':         'up',
};

/** Animation name mapping: our names → PixelLab folder patterns */
const ANIM_PATTERNS: Record<string, string[]> = {
  'idle':   ['idle', 'fight-stance', 'stance'],
  'walk':   ['running', 'walk', 'run'],
  'attack': ['attack', 'uppercut', 'slash', 'punch', 'fight', 'strike'],
};

interface AnimFrames {
  /** Per-direction frame arrays. Key = Direction8 */
  dirs: Map<Direction8, HTMLImageElement[]>;
}

interface EnemySpriteData {
  /** Static rotation images per direction */
  rotations: Map<Direction8, HTMLImageElement>;
  /** Animation frames: animName → direction → frames[] */
  animations: Map<string, AnimFrames>;
  loaded: boolean;
  frameSize: number;
}

class EnemySpriteRegistry {
  private entries: Map<string, EnemySpriteData> = new Map();
  private loading: Map<string, Promise<void>> = new Map();

  load(typeId: string): Promise<void> {
    if (this.entries.has(typeId) && this.entries.get(typeId)!.loaded) {
      return Promise.resolve();
    }
    if (this.loading.has(typeId)) return this.loading.get(typeId)!;

    const promise = this.loadFromPixelLab(typeId)
      .then(() => { this.loading.delete(typeId); })
      .catch(() => { this.loading.delete(typeId); this.entries.delete(typeId); });

    this.loading.set(typeId, promise);
    return promise;
  }

  private async loadFromPixelLab(typeId: string): Promise<void> {
    const basePath = `sprites/enemies/${typeId}`;

    // Load metadata
    let meta: any;
    try {
      const resp = await fetch(`${basePath}/metadata.json`);
      if (!resp.ok) return;
      meta = await resp.json();
    } catch {
      return; // No PixelLab export
    }

    const entry: EnemySpriteData = {
      rotations: new Map(),
      animations: new Map(),
      loaded: false,
      frameSize: meta.character?.size?.width || 48,
    };

    // Load rotation images (8 directions)
    const rotPromises: Promise<void>[] = [];
    if (meta.frames?.rotations) {
      for (const [plDir, relPath] of Object.entries(meta.frames.rotations)) {
        const dir = PL_TO_DIR[plDir];
        if (!dir) continue;
        rotPromises.push(
          this.loadImage(`${basePath}/${relPath}`).then(img => {
            if (img) entry.rotations.set(dir, img);
          })
        );
      }
    }
    await Promise.all(rotPromises);

    // Load animation frames
    if (meta.frames?.animations) {
      for (const [animFolderName, dirFrames] of Object.entries(meta.frames.animations) as [string, any][]) {
        // Match PixelLab animation name to our animation types
        const ourAnimName = this.matchAnimName(animFolderName);
        if (!ourAnimName) continue;

        const animData: AnimFrames = { dirs: new Map() };

        const animPromises: Promise<void>[] = [];
        for (const [plDir, framePaths] of Object.entries(dirFrames) as [string, string[]][]) {
          const dir = PL_TO_DIR[plDir];
          if (!dir) continue;

          const framePromises = framePaths.map(fp => this.loadImage(`${basePath}/${fp}`));
          animPromises.push(
            Promise.all(framePromises).then(imgs => {
              const valid = imgs.filter((i): i is HTMLImageElement => i !== null);
              if (valid.length > 0) animData.dirs.set(dir, valid);
            })
          );
        }
        await Promise.all(animPromises);

        if (animData.dirs.size > 0) {
          entry.animations.set(ourAnimName, animData);
        }
      }
    }

    if (entry.rotations.size > 0) {
      entry.loaded = true;
      this.entries.set(typeId, entry);
      debugLog('SpriteRegistry', `Loaded ${typeId}: ${entry.rotations.size} dirs, ${entry.animations.size} anims`);
    }
  }

  /** Match a PixelLab animation folder name to our animation type */
  private matchAnimName(folderName: string): string | null {
    const lower = folderName.toLowerCase();
    for (const [ourName, patterns] of Object.entries(ANIM_PATTERNS)) {
      for (const pat of patterns) {
        if (lower.includes(pat)) return ourName;
      }
    }
    return null;
  }

  private loadImage(src: string): Promise<HTMLImageElement | null> {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = src;
    });
  }

  loadForZone(zoneId: string): Promise<void> {
    const zone = ZONES[zoneId as ZoneType];
    if (!zone) return Promise.resolve();
    const typeIds = new Set<string>();
    for (const spawn of zone.enemySpawns) typeIds.add(spawn.type);
    if (zone.bossType) typeIds.add(zone.bossType);
    return Promise.all(Array.from(typeIds).map(id => this.load(id))).then(() => {
      debugLog('SpriteRegistry', `Zone ${zoneId}: ${typeIds.size} types queued`);
    });
  }

  unloadExcept(activeZoneIds: string[]): void {
    const activeTypes = new Set<string>();
    for (const zoneId of activeZoneIds) {
      const zone = ZONES[zoneId as ZoneType];
      if (!zone) continue;
      for (const spawn of zone.enemySpawns) activeTypes.add(spawn.type);
      if (zone.bossType) activeTypes.add(zone.bossType);
    }
    for (const [typeId] of this.entries) {
      if (!activeTypes.has(typeId)) this.entries.delete(typeId);
    }
  }

  /**
   * Draw an enemy sprite with animation support.
   * Returns: 0 = not drawn, 1 = static rotation, 2 = frame animation
   */
  draw(
    typeId: string,
    ctx: CanvasRenderingContext2D,
    direction: Direction8,
    destX: number,
    destY: number,
    destWidth: number,
    destHeight: number,
    animName?: string,
    elapsed?: number,
    fps?: number,
  ): number {
    const entry = this.entries.get(typeId);
    if (!entry || !entry.loaded) return 0;

    // Snap 8 directions to 4 cardinal
    const dir4 = DIR8_TO_DIR4[direction];

    let img: HTMLImageElement | null = null;

    // Try animation frames for this cardinal direction
    let usedFrameAnim = false;
    if (animName && elapsed !== undefined) {
      const anim = entry.animations.get(animName);
      if (anim) {
        const frames = anim.dirs.get(dir4);
        if (frames && frames.length > 0) {
          const frameRate = fps || 8;
          const frameIdx = Math.floor(elapsed * frameRate) % frames.length;
          img = frames[frameIdx];
          usedFrameAnim = true;
        }
      }
    }

    // No frame anim → use rotation image (4-dir snapped)
    if (!img) {
      img = entry.rotations.get(dir4) || entry.rotations.get('down') || null;
    }

    if (!img) return 0; // 0 = not drawn

    ctx.drawImage(img, destX, destY, destWidth, destHeight);
    return usedFrameAnim ? 2 : 1; // 2 = frame anim, 1 = static rotation
  }

  isLoaded(typeId: string): boolean {
    const entry = this.entries.get(typeId);
    return entry ? entry.loaded : false;
  }

  getFrameSize(typeId: string): number {
    const entry = this.entries.get(typeId);
    return entry ? entry.frameSize : 48;
  }
}

// Singleton
let _instance: EnemySpriteRegistry | null = null;

export function getEnemySpriteRegistry(): EnemySpriteRegistry {
  if (!_instance) {
    _instance = new EnemySpriteRegistry();
  }
  return _instance;
}
